import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Wrench, Shield, Clock, ChevronRight, Loader2, Gauge, ExternalLink } from 'lucide-react';
import { apiRequest } from '../../../api/core.js';
import { useFormat } from '../../../i18n/format.js';

/**
 * VehicleIssueDetailModal - Drill-down modal for grouped vehicle issues
 *
 * When a vehicle has multiple issues of the same type (e.g. "5 elements critiques"),
 * this modal lists each individual item so the user can address them one by one.
 */

// Human-readable resource type labels
const RESOURCE_TYPE_LABELS = {
  fuel_level: 'Niveau carburant',
  oil_level: 'Niveau huile',
  coolant_level: 'Niveau liquide refroidissement',
  brake_fluid: 'Liquide de frein',
  washer_fluid: 'Lave-glace',
  battery_health: 'Batterie',
  tire_front_left: 'Pneu avant gauche',
  tire_front_right: 'Pneu avant droit',
  tire_rear_left: 'Pneu arrière gauche',
  tire_rear_right: 'Pneu arrière droit',
  brake_pad_front: 'Plaquettes avant',
  brake_pad_rear: 'Plaquettes arrière',
  air_filter: 'Filtre à air',
  cabin_filter: 'Filtre habitacle',
  transmission_fluid: 'Huile transmission',
  adblue_level: 'AdBlue',
  dpf_status: 'Filtre à particules',
  windshield: 'Pare-brise',
  body_condition: 'État carrosserie',
  headlights: 'Phares',
  taillights: 'Feux arrière',
};

const ISSUE_TYPE_CONFIG = {
  resource_critical: {
    title: 'Éléments critiques',
    icon: Gauge,
    color: 'from-red-500 to-red-600',
    itemColor: 'border-red-200 bg-red-50',
    badgeColor: 'bg-red-100 text-red-700'
  },
  resource_warning: {
    title: 'Éléments à surveiller',
    icon: Gauge,
    color: 'from-amber-500 to-amber-600',
    itemColor: 'border-amber-200 bg-amber-50',
    badgeColor: 'bg-amber-100 text-amber-700'
  },
  claim_open: {
    title: 'Sinistres en cours',
    icon: AlertTriangle,
    color: 'from-orange-500 to-orange-600',
    itemColor: 'border-orange-200 bg-orange-50',
    badgeColor: 'bg-orange-100 text-orange-700'
  },
  maintenance_overdue: {
    title: 'Maintenances en retard',
    icon: Wrench,
    color: 'from-red-500 to-red-600',
    itemColor: 'border-red-200 bg-red-50',
    badgeColor: 'bg-red-100 text-red-700'
  },
  maintenance_due: {
    title: 'Maintenances à venir',
    icon: Clock,
    color: 'from-blue-500 to-blue-600',
    itemColor: 'border-blue-200 bg-blue-50',
    badgeColor: 'bg-blue-100 text-blue-700'
  },
  insurance_not_verified: {
    title: 'Assurance',
    icon: Shield,
    color: 'from-purple-500 to-purple-600',
    itemColor: 'border-purple-200 bg-purple-50',
    badgeColor: 'bg-purple-100 text-purple-700'
  }
};

// Maintenance type labels
const MAINT_TYPE_LABELS = {
  oil_change: 'Vidange',
  tire_rotation: 'Rotation pneus',
  brake_service: 'Freins',
  engine_repair: 'Moteur',
  transmission: 'Transmission',
  electrical: 'Électrique',
  bodywork: 'Carrosserie',
  inspection: 'Inspection',
  emergency: 'Urgence',
  other: 'Autre'
};

function VehicleIssueDetailModal({ issue, vehicle, onClose, onItemClick }) {
  const [loading, setLoading] = useState(false);
  const [detailItems, setDetailItems] = useState([]);

  const config = ISSUE_TYPE_CONFIG[issue?.type] || ISSUE_TYPE_CONFIG.resource_critical;
  const IconComp = config.icon;

  useEffect(() => {
    if (!issue) return;

    // If the issue already has individual items embedded, use them
    if (issue.items && issue.items.length > 0) {
      setDetailItems(issue.items);
      return;
    }

    // For resource alerts, fetch individual readings directly
    // Uses the same raw status field as the summary endpoint (critical/warning/replace)
    if ((issue.type === 'resource_critical' || issue.type === 'resource_warning') && issue.data?.vehicleId) {
      const fetchAlerts = async () => {
        setLoading(true);
        try {
          const vid = issue.data.vehicleId;
          const statuses = issue.type === 'resource_critical'
            ? ['critical', 'replace']
            : ['warning'];
          // Fetch ALL readings for vehicle, sorted by date DESC, then keep only latest per resource_type
          // This ensures old stale critical readings are superseded by newer OK readings
          const allRes = await apiRequest(`/vehicle_resource_reading?vehicle_id=${vid}&_limit=200&_sort=-reading_date`);
          const allReadings = (allRes.ok && Array.isArray(allRes.data)) ? allRes.data : [];
          // Keep only the latest reading per resource_type
          const latestByType = new Map();
          for (const r of allReadings) {
            if (!latestByType.has(r.resource_type)) latestByType.set(r.resource_type, r);
          }
          // Filter to only those matching the alert statuses
          const readings = [...latestByType.values()].filter(r => statuses.includes(r.status));
          const items = readings.map(r => ({
            id: r.id,
            resource_name: RESOURCE_TYPE_LABELS[r.resource_type] || r.resource_type,
            resource_type: r.resource_type,
            current_value: r.value_percent != null ? `${r.value_percent}%` : r.value_raw,
            status: r.status,
            last_reading_date: r.reading_date,
            odometer_km: r.odometer_km,
            vehicle_id: r.vehicle_id,
          }));
          setDetailItems(items);
        } catch (err) {
          console.error('Failed to fetch resource alerts:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchAlerts();
    }
  }, [issue]);

  if (!issue) return null;

  const vehicleLabel = vehicle
    ? `${vehicle.make} ${vehicle.model} — ${vehicle.license_plate}`
    : '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-[560px] max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={`bg-gradient-to-r ${config.color} text-white px-5 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <IconComp className="w-5 h-5" />
            <div>
              <h2 className="text-lg font-semibold">{config.title}</h2>
              {vehicleLabel && (
                <p className="text-white/70 text-sm">{vehicleLabel}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary bar */}
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {issue.itemCount} élément{issue.itemCount > 1 ? 's' : ''} à traiter
          </span>
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${config.badgeColor}`}>
            {issue.title}
          </span>
        </div>

        {/* Item list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Chargement des détails...</span>
            </div>
          ) : detailItems.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <IconComp className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucun détail disponible</p>
            </div>
          ) : (
            detailItems.map((item, idx) => (
              <DetailItemRow
                key={item.id || idx}
                item={item}
                issueType={issue.type}
                config={config}
                onClick={() => onItemClick && onItemClick(issue.type, item)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Individual item row — adapts display based on issue type
 */
function DetailItemRow({ item, issueType, config, onClick }) {
  const { fmt } = useFormat();
  // Resource alert item
  if (issueType === 'resource_critical' || issueType === 'resource_warning') {
    const statusBadge = item.status === 'critical' || item.status === 'replace'
      ? 'bg-red-100 text-red-700'
      : item.status === 'warning'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-blue-100 text-blue-700';
    return (
      <button
        onClick={onClick}
        className={`w-full flex items-center gap-3 p-3 rounded-lg border ${config.itemColor} text-left hover:shadow-sm transition-all group`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900">
              {item.resource_name || item.resource_type || `Ressource #${item.id}`}
            </p>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${statusBadge}`}>
              {item.status === 'replace' ? 'Remplacement' : item.status === 'critical' ? 'Critique' : item.status === 'warning' ? 'Attention' : 'Échéance'}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            {item.current_value != null && (
              <span className="text-xs text-gray-500">
                Valeur: <span className="font-medium text-gray-700">{item.current_value}</span>
              </span>
            )}
            {item.threshold_value != null && (
              <span className="text-xs text-gray-500">
                Seuil: <span className="font-medium">{item.threshold_value}</span>
              </span>
            )}
            {item.odometer_km && (
              <span className="text-xs text-gray-400">
                {item.odometer_km.toLocaleString()} km
              </span>
            )}
            {item.last_reading_date && (
              <span className="text-xs text-gray-400">
                {fmt.date(item.last_reading_date)}
              </span>
            )}
          </div>
        </div>
        <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors" />
      </button>
    );
  }

  // Claim item
  if (issueType === 'claim_open') {
    return (
      <button
        onClick={onClick}
        className={`w-full flex items-center gap-3 p-3 rounded-lg border ${config.itemColor} text-left hover:shadow-sm transition-all group`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            Sinistre #{item.id} — {item.type || 'Non classé'}
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${config.badgeColor}`}>
              {item.status || 'ouvert'}
            </span>
            {item.event_date && (
              <span className="text-xs text-gray-400">
                {fmt.date(item.event_date)}
              </span>
            )}
            {item.description && (
              <span className="text-xs text-gray-500 truncate">
                {item.description.substring(0, 40)}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors" />
      </button>
    );
  }

  // Maintenance item
  if (issueType === 'maintenance_overdue' || issueType === 'maintenance_due') {
    const typeLabel = MAINT_TYPE_LABELS[item.type] || item.type || 'Maintenance';
    const daysInfo = item._daysOverdue
      ? `${item._daysOverdue} jour(s) de retard`
      : item._daysUntil
        ? `Dans ${item._daysUntil} jour(s)`
        : '';
    return (
      <button
        onClick={onClick}
        className={`w-full flex items-center gap-3 p-3 rounded-lg border ${config.itemColor} text-left hover:shadow-sm transition-all group`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            {item.title || typeLabel}
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${config.badgeColor}`}>
              {typeLabel}
            </span>
            {daysInfo && (
              <span className="text-xs text-gray-500">{daysInfo}</span>
            )}
            {item.scheduled_date && (
              <span className="text-xs text-gray-400">
                {fmt.date(item.scheduled_date)}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors" />
      </button>
    );
  }

  // Fallback
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50 text-left hover:shadow-sm transition-all group"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">
          {item.title || item.name || `Élément #${item.id || '?'}`}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
    </button>
  );
}

export default VehicleIssueDetailModal;
